import { type JSX, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';

import { HelpContent } from './help-content';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-in drawer triggered from the topbar "?" button. Same FAQ
 * surface as the /help page but in a narrower column without the
 * category sidebar (search-first). Has a "Open full help center"
 * link for users who want to browse by category.
 */
export function HelpDrawer({ open, onClose }: HelpDrawerProps): JSX.Element | null {
  const { t } = useTranslation('default');

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      {/* Panel */}
      <aside className="relative z-10 w-full max-w-[440px] h-full bg-surface border-l border-line shadow-2xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-line flex-shrink-0">
          <div>
            <h2 className="display text-[16px] font-semibold text-text leading-none">
              {t('help_title')}
            </h2>
            <Link
              to="/help"
              onClick={onClose}
              className="text-[12px] text-text-3 hover:text-volt-600 hover:underline mt-1 inline-block"
            >
              {t('help_open_full')}
            </Link>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-md text-text-3 hover:bg-surface-3 hover:text-text"
            aria-label={t('cancel')}
          >
            <Icon.chevronLeft width="16" height="16" className="rotate-90" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          <HelpContent layout="drawer" />
        </div>
      </aside>
    </div>
  );
}
